import React, { useState, useRef, useCallback } from 'react';
import { 
    Upload, X, Image, Music, Video, File, 
    CheckCircle, AlertCircle, Loader 
} from 'lucide-react';
import api from '../services/api';

const FileUpload = ({
    type = 'image',
    onUpload,
    onError,
    maxSizeMB = 5,
    accept = '',
    className = '',
    children,
    showPreview = true,
    multiple = false,
    endpoint = null,
    isProfileImage = false,
    contributingArtists = [], // Array of contributing artist objects
    onContributingArtistsChange = null, // Callback for when contributing artists change
    // For callers passing a short height via className (e.g. a cover-image
    // slot next to a small thumbnail preview) — the default layout's icon +
    // two lines of text + p-6 padding needs ~170px and just overflows past
    // any shorter wrapper instead of respecting it. Compact mode shrinks
    // everything down and actually fills that height instead of spilling
    // into whatever sits below it on the page.
    compact = false
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    // File type configurations
    const fileTypes = {
        image: {
            accept: accept || 'image/*',
            maxSize: maxSizeMB || 5,
            icon: Image,
            label: 'image'
        },
        audio: {
            accept: accept || 'audio/*',
            maxSize: maxSizeMB || 50,
            icon: Music,
            label: 'audio file'
        },
        video: {
            accept: accept || 'video/*',
            maxSize: maxSizeMB || 100,
            icon: Video,
            label: 'video'
        },
        any: {
            accept: accept || '*/*',
            maxSize: maxSizeMB || 10,
            icon: File,
            label: 'file'
        }
    };

    const config = fileTypes[type] || fileTypes.any;

    // Validate file
    const validateFile = (file) => {
        // Check file size
        if (file.size > config.maxSize * 1024 * 1024) {
            throw new Error(`File too large. Maximum size is ${config.maxSize}MB.`);
        }

        // Check file type if specified
        if (config.accept !== '*/*' && !file.type.match(config.accept.replace('/*', '/.*'))) {
            throw new Error(`Invalid file type. Please select a valid ${config.label}.`);
        }

        return true;
    };

    // Create preview for images
    const createPreview = (file) => {
        if (type === 'image' && showPreview) {
            const reader = new FileReader();
            reader.onload = (e) => setPreview(e.target.result);
            reader.readAsDataURL(file);
        } else {
            setPreview({
                name: file.name,
                size: file.size,
                type: file.type
            });
        }
    };

    // Handle file selection with retry logic
    const handleFiles = useCallback(async (files) => {
        const fileList = Array.from(files);
        
        if (!multiple && fileList.length > 1) {
            onError?.('Please select only one file.');
            return;
        }

        for (const file of fileList) {
            let retryCount = 0;
            // Video uploads are large — never retry from scratch on timeout
            const maxRetries = type === 'video' ? 1 : 3;
            
            while (retryCount < maxRetries) {
                try {
                    validateFile(file);
                    createPreview(file);
                    
                    setUploading(true);
                    setUploadProgress(0);

                    console.log(`Upload attempt ${retryCount + 1} for file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                    
                    await uploadFile(file);
                    break; // Success, exit retry loop
                    
                } catch (error) {
                    retryCount++;
                    console.error(`Upload attempt ${retryCount} failed:`, error.message);
                    
                    if (retryCount >= maxRetries) {
                        console.error('Upload failed after all retries:', error);
                        onError?.(error.message || 'Upload failed');
                        setPreview(null);
                    } else {
                        console.log(`Retrying upload in ${retryCount * 2} seconds...`);
                        setUploadProgress(0);
                        await new Promise(resolve => setTimeout(resolve, retryCount * 2000));
                    }
                } finally {
                    if (retryCount >= maxRetries || retryCount === 0) {
                        setUploading(false);
                        setUploadProgress(0);
                    }
                }
            }
        }
    }, [type, multiple, onUpload, onError, config.maxSize]);

    // Separate upload function for retry logic
    const uploadFile = useCallback(async (file) => {
        // Create FormData
        const formData = new FormData();
        const fieldName = type === 'image' ? 'image' : type === 'audio' ? 'audio' : 'video';
        formData.append(fieldName, file);

        // Upload file with real progress tracking
        let uploadEndpoint;
        if (endpoint) {
            // Use custom endpoint if provided
            uploadEndpoint = endpoint;
        } else if (type === 'image' && isProfileImage) {
            // Profile image upload
            uploadEndpoint = '/api/upload/image/profile';
        } else if (type === 'image') {
            // Generic image upload (cover art, thumbnails, etc.)
            uploadEndpoint = '/api/upload/image';
        } else {
            // Audio/video upload
            uploadEndpoint = `/api/upload/${type}`;
        }

        // Use XMLHttpRequest for real upload progress
        const response = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            // Track upload progress with throttling
            let lastProgressUpdate = 0;
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const now = Date.now();
                    // Throttle progress updates to every 100ms for better performance
                    if (now - lastProgressUpdate >= 100) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        setUploadProgress(progress);
                        lastProgressUpdate = now;
                    }
                }
            });

            // Handle response
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const result = JSON.parse(xhr.responseText);
                        resolve(result);
                    } catch (e) {
                        reject(new Error('Invalid response format'));
                    }
                } else {
                    try {
                        const error = JSON.parse(xhr.responseText);
                        reject(new Error(error.message || `Upload failed with status ${xhr.status}`));
                    } catch (e) {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                }
            });

            // Handle errors
            xhr.addEventListener('error', () => {
                reject(new Error('Network error occurred'));
            });

            // Handle timeout
            xhr.addEventListener('timeout', () => {
                reject(new Error('Upload timed out. Check your connection and try again.'));
            });

            // Video uploads can be several GB — allow 4 hours; other types 20 minutes
            xhr.timeout = type === 'video' ? 4 * 60 * 60 * 1000 : 20 * 60 * 1000;

            // Start the upload
            xhr.open('POST', uploadEndpoint);
            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('deepwave_token')}`);
            xhr.send(formData);
        });

        if (response.success) {
            // If upload succeeded and we have contributing artists, add them
            if (contributingArtists.length > 0 && response.data.id) {
                try {
                    const contributingArtistIds = contributingArtists.map(artist => artist.user_id);
                    await api.post(`/contributing-artists/upload/${response.data.id}`, {
                        contributingArtistIds
                    });
                } catch (contributingError) {
                    console.error('Error adding contributing artists to upload:', contributingError);
                    // Don't fail the upload for this
                }
            }
            
            onUpload?.(response.data);
        } else {
            throw new Error(response.message || 'Upload failed');
        }
    }, [type, endpoint, isProfileImage, onUpload, contributingArtists]);

    // Handle drag events
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFiles(files);
        }
    }, [handleFiles]);

    // Handle input change
    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    // Clear preview
    const clearPreview = () => {
        setPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const IconComponent = config.icon;

    return (
        <div className={`relative ${className}`}>
            {/* Main Upload Area */}
            <div
                className={`
                    relative border-2 border-dashed rounded-lg text-center transition-all duration-300 cursor-pointer
                    ${compact ? 'h-full p-2 flex flex-col items-center justify-center overflow-hidden' : 'p-6'}
                    ${isDragging
                        ? 'border-blue-600 bg-blue-600/10'
                        : 'border-gray-600 hover:border-blue-600 hover:bg-blue-600/5'
                    }
                    ${uploading ? 'pointer-events-none' : ''}
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
            >
                {/* Hidden File Input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={config.accept}
                    multiple={multiple}
                    onChange={handleInputChange}
                    className="hidden"
                />

                {/* Upload Content */}
                {!preview && !uploading && (
                    <div className={compact ? 'space-y-1' : 'space-y-4'}>
                        <IconComponent className={compact ? 'w-5 h-5 text-gray-400 mx-auto' : 'w-12 h-12 text-gray-400 mx-auto'} />
                        <div>
                            <p className={compact ? 'text-xs font-medium text-gray-300 leading-tight' : 'text-lg font-semibold text-gray-300'}>
                                {children || (
                                    <>
                                        Drop your {config.label} here or{' '}
                                        <span className="text-blue-400">browse</span>
                                    </>
                                )}
                            </p>
                            {!compact && (
                                <p className="text-sm text-gray-500 mt-2">
                                    Maximum size: {config.maxSize}MB
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <div className={compact ? 'space-y-1 w-full' : 'space-y-4'}>
                        <Loader className={compact ? 'w-5 h-5 text-blue-600 animate-spin mx-auto' : 'w-12 h-12 text-blue-600 animate-spin mx-auto'} />
                        <div>
                            {!compact && (
                                <p className="text-lg font-semibold text-gray-300">
                                    {uploadProgress === 0 ? 'Preparing upload...' :
                                     uploadProgress < 100 ? 'Uploading...' :
                                     'Processing...'}
                                </p>
                            )}
                            <div className={`w-full bg-gray-700 rounded-full overflow-hidden ${compact ? 'h-1.5' : 'h-3 mt-3'}`}>
                                <div
                                    className={`rounded-full transition-all duration-300 ${compact ? 'h-1.5' : 'h-3'} ${
                                        uploadProgress === 100 ? 'bg-green-500' : 'bg-blue-600'
                                    }`}
                                    style={{ width: `${Math.max(uploadProgress, 5)}%` }}
                                />
                            </div>
                            {compact ? (
                                <p className="text-[10px] text-gray-400 mt-1">{uploadProgress}%</p>
                            ) : (
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-sm text-gray-400">
                                        {uploadProgress}% complete
                                    </p>
                                    {uploadProgress === 100 && (
                                        <p className="text-xs text-green-400">
                                            Finalizing upload...
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Preview */}
                {preview && !uploading && (
                    <div className={compact ? 'space-y-1' : 'space-y-4'}>
                        {type === 'image' && typeof preview === 'string' ? (
                            <div className="relative inline-block">
                                <img
                                    src={preview}
                                    alt="Preview"
                                    width={compact ? 48 : 128}
                                    height={compact ? 48 : 128}
                                    className={compact ? 'w-12 h-12 object-cover rounded-lg mx-auto' : 'w-32 h-32 object-cover rounded-lg mx-auto'}
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearPreview();
                                    }}
                                    aria-label="Remove image"
                                    className={compact
                                        ? 'absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors'
                                        : 'absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors'}
                                >
                                    <X className={compact ? 'w-2.5 h-2.5 text-white' : 'w-4 h-4 text-white'} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center space-x-3">
                                <IconComponent className={compact ? 'w-5 h-5 text-green-500' : 'w-8 h-8 text-green-500'} />
                                {!compact && (
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-300">
                                            {preview.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(preview.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </div>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearPreview();
                                    }}
                                    aria-label="Remove file"
                                    className={compact
                                        ? 'w-4 h-4 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors'
                                        : 'w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors'}
                                >
                                    <X className={compact ? 'w-2.5 h-2.5 text-white' : 'w-4 h-4 text-white'} />
                                </button>
                            </div>
                        )}

                        {compact ? (
                            <div className="flex items-center justify-center gap-1 text-green-400">
                                <CheckCircle className="w-3 h-3" />
                                <span className="text-[10px] font-medium">Ready</span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center space-x-2 text-green-400">
                                <CheckCircle className="w-5 h-5" />
                                <span className="text-sm font-semibold">Ready to upload</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Drag Overlay */}
            {isDragging && (
                <div className="absolute inset-0 bg-blue-600/20 border-2 border-blue-600 border-dashed rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <Upload className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-lg font-semibold text-blue-300">
                            Drop to upload
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;